/* eslint-disable no-console */
import { AuthN, getConfig } from '@seek/online-identity';

import { getLogger } from 'src/modules/logger';
import { environments } from 'src/siteConfig';
import type { ConfigParams } from 'src/types';

const getError = (err: unknown) => (err as { error?: string }).error;

export const createSsoClient = ({ environment }: ConfigParams) => {
  const authN = new AuthN(getConfig('seekAnz-hirer', window.location));
  const { logError } = getLogger();

  const ssoClient = {
    login: () =>
      authN.login({
        returnUri: window.location.pathname + window.location.search,
      }),
    getToken: async () => {
      try {
        // This override is to support insertion of override tokens for integration testing
        const idTokenOverride =
          window.localStorage.getItem('id-token-override');

        if (Boolean(idTokenOverride)) {
          return Promise.resolve(idTokenOverride);
        }

        const token = await authN.getToken();
        return token || null;
      } catch (err) {
        const error = getError(err);
        if (error === 'login_required' || error === 'invalid_grant') {
          return null;
        }

        logError(err, { note: 'SSO Client Error' });

        throw err;
      }
    },
    getUniqueId: async () => {
      try {
        const identity = await authN.getIdentity();

        if (!identity || !identity.sub) {
          return null;
        }

        return identity.sub.ID;
      } catch (err) {
        const error = getError(err);
        if (error === 'login_required' || error === 'invalid_grant') {
          return null;
        }
        logError(err, { note: 'SSO Client Error' });
        throw err;
      }
    },
  };

  if (environment === environments.development) {
    const mockLoggedIn = true;
    console.warn(
      'Mocking SSO calls',
      mockLoggedIn ? 'Logged in' : 'Logged out',
    );
    ssoClient.getToken = () =>
      Promise.resolve(mockLoggedIn ? 'mockToken' : null);
    ssoClient.getUniqueId = () =>
      Promise.resolve(mockLoggedIn ? 'mockUniqueId' : null);
  }

  return ssoClient;
};
